import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, RefreshCw, XCircle, Rewind, FileCode, AlertTriangle, Loader2, Check, X, FileMinus, FilePlus } from 'lucide-react';
import { api } from '../../api/client';

type Line = {
  id: string;
  lineNumber: number;
  productName: string;
  productCode: string | null;
  quantity: string;
  unitCode: string;
  unitPrice: string;
  discountPct: string | null;
  lineTotal: string;
  vatCategory: string;
  vatPercent: string;
  vatAmount: string;
};

type Invoice = {
  id: string;
  tenantId: string;
  direction: 'outbound' | 'inbound';
  efakturaId: number | null;
  status: string;
  cirStatus: string | null;
  invoiceNumber: string;
  invoiceTypeCode: string;
  issueDate: string;
  dueDate: string | null;
  deliveryDate: string | null;
  currency: string;
  buyerName: string;
  buyerTaxId: string;
  buyerRegNumber: string | null;
  buyerJbkjs: string | null;
  buyerAddress: string | null;
  buyerCity: string | null;
  buyerEmail: string | null;
  isBudgetClient: boolean | null;
  totalWithoutVat: string;
  totalVat: string;
  totalWithVat: string;
  paymentMeansCode: string | null;
  bankAccount: string | null;
  purchaseOrderRef: string | null;
  contractRef: string | null;
  precedingInvoice: string | null;
  ublXml: string | null;
  requestId: string | null;
  cancelComment: string | null;
  stornoComment: string | null;
  stornoNumber: string | null;
  errorCode: string | null;
  portalErrors: { message?: string } | null;
  version: number | null;
  lastModifiedUtc: string | null;
  lastSyncAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: Line[];
};

const statusColors: Record<string, { bg: string; color: string }> = {
  draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  queued: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
  submitting: { bg: 'rgba(59,130,246,0.25)', color: '#93c5fd' },
  submitted: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
  sent: { bg: 'rgba(234,179,8,0.2)', color: '#facc15' },
  approved: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80' },
  rejected: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5' },
  cancelled: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af' },
  storno: { bg: 'rgba(249,115,22,0.2)', color: '#fb923c' },
  submit_failed: { bg: 'rgba(239,68,68,0.25)', color: '#fca5a5' },
};

const canSubmit = (s: string) => s === 'draft' || s === 'submit_failed';
const canCancel = (s: string) => s === 'submitted' || s === 'sent';
const canStorno = (s: string) => s === 'approved' || s === 'sent';
const canRefresh = (s: string) => s !== 'draft';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showXml, setShowXml] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<Invoice>(`/admin/efaktura/invoices/${id}`);
      setInvoice(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju');
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const doSubmit = async () => {
    if (!invoice || !window.confirm('Poslati fakturu na SEF portal?')) return;
    setBusy('submit');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ efakturaId: number }>(`/admin/efaktura/invoices/${invoice.id}/submit`);
      setNotice(`Poslato. SEF Invoice ID: ${res.efakturaId}`);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri slanju');
    } finally {
      setBusy(null);
    }
  };

  const doRefreshStatus = async () => {
    if (!invoice) return;
    setBusy('refresh');
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ sefStatus: string; localStatus: string }>(`/admin/efaktura/invoices/${invoice.id}/refresh-status`);
      setNotice(`SEF status: ${res.sefStatus}`);
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri osvežavanju');
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    if (!invoice) return;
    const comment = window.prompt('Komentar za otkazivanje:');
    if (!comment) return;
    setBusy('cancel');
    setError(null);
    setNotice(null);
    try {
      await api.post(`/admin/efaktura/invoices/${invoice.id}/cancel`, { comment });
      setNotice('Faktura otkazana.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri otkazivanju');
    } finally {
      setBusy(null);
    }
  };

  const doStorno = async () => {
    if (!invoice) return;
    const stornoNumber = window.prompt('Broj storno dokumenta:');
    if (!stornoNumber) return;
    const comment = window.prompt('Komentar za storno:');
    if (!comment) return;
    setBusy('storno');
    setError(null);
    setNotice(null);
    try {
      await api.post(`/admin/efaktura/invoices/${invoice.id}/storno`, { stornoNumber, comment });
      setNotice('Storno poslat.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri storniranju');
    } finally {
      setBusy(null);
    }
  };

  const doAcceptInbound = async () => {
    if (!invoice || !window.confirm('Prihvatiti ulaznu fakturu?')) return;
    setBusy('accept');
    setError(null);
    setNotice(null);
    try {
      await api.post(`/admin/efaktura/inbound/${invoice.id}/accept`, {});
      setNotice('Faktura prihvaćena.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri prihvatanju');
    } finally {
      setBusy(null);
    }
  };

  const doRejectInbound = async () => {
    if (!invoice) return;
    const comment = window.prompt('Razlog odbijanja:');
    if (!comment) return;
    setBusy('reject');
    setError(null);
    setNotice(null);
    try {
      await api.post(`/admin/efaktura/inbound/${invoice.id}/reject`, { comment });
      setNotice('Faktura odbijena.');
      await refresh();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri odbijanju');
    } finally {
      setBusy(null);
    }
  };

  if (!invoice) {
    return (
      <div>
        {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
        {!error && <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>}
      </div>
    );
  }

  const sc = statusColors[invoice.status] || statusColors.draft;
  const fmt = (n: string | number) => new Intl.NumberFormat('sr-Latn-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('sr-Latn-RS') : '-';
  const isInbound = invoice.direction === 'inbound';
  const canReviewInbound = isInbound && invoice.status === 'submitted';
  const canCreateNote = !isInbound
    && (invoice.status === 'approved' || invoice.status === 'sent' || invoice.status === 'submitted')
    && invoice.invoiceTypeCode === '380';

  return (
    <div>
      <button onClick={() => navigate(`/efaktura${isInbound ? '?tab=inbound' : ''}`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <ArrowLeft size={14} /> E-Fakture
      </button>

      <header style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            Faktura {invoice.invoiceNumber}
            <span style={{ marginLeft: 12, fontSize: 11, padding: '3px 10px', borderRadius: 10, background: sc.bg, color: sc.color, fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle' }}>
              {invoice.status}
            </span>
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Tip {invoice.invoiceTypeCode} · {fmtDate(invoice.issueDate)}
            {invoice.efakturaId && <> · SEF ID: {invoice.efakturaId}</>}
            {invoice.cirStatus && invoice.cirStatus !== 'None' && <> · CIR: {invoice.cirStatus}</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isInbound && canSubmit(invoice.status) && (
            <button onClick={doSubmit} disabled={busy !== null} style={btnPrimary}>
              {busy === 'submit' ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Pošalji
            </button>
          )}
          {canReviewInbound && (
            <>
              <button onClick={doAcceptInbound} disabled={busy !== null} style={btnPrimary}>
                {busy === 'accept' ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Prihvati
              </button>
              <button onClick={doRejectInbound} disabled={busy !== null} style={btnDanger}>
                {busy === 'reject' ? <Loader2 size={14} className="spin" /> : <X size={14} />} Odbij
              </button>
            </>
          )}
          {canRefresh(invoice.status) && (
            <button onClick={doRefreshStatus} disabled={busy !== null} style={btnSecondary}>
              {busy === 'refresh' ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Osveži
            </button>
          )}
          {!isInbound && canCancel(invoice.status) && (
            <button onClick={doCancel} disabled={busy !== null} style={btnDanger}>
              <XCircle size={14} /> Otkaži
            </button>
          )}
          {!isInbound && canStorno(invoice.status) && (
            <button onClick={doStorno} disabled={busy !== null} style={btnDanger}>
              <Rewind size={14} /> Storno
            </button>
          )}
          {canCreateNote && (
            <>
              <button onClick={() => navigate(`/efaktura/invoices/new?clone=${invoice.id}&type=381`)} style={btnSecondary} title="Knjižno odobrenje">
                <FileMinus size={14} /> Odobrenje
              </button>
              <button onClick={() => navigate(`/efaktura/invoices/new?clone=${invoice.id}&type=383`)} style={btnSecondary} title="Knjižno zaduženje">
                <FilePlus size={14} /> Zaduženje
              </button>
            </>
          )}
          {invoice.ublXml && (
            <button onClick={() => setShowXml((v) => !v)} style={btnSecondary}>
              <FileCode size={14} /> {showXml ? 'Sakrij XML' : 'Prikaži XML'}
            </button>
          )}
        </div>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      {invoice.portalErrors?.message && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> SEF portal: {invoice.portalErrors.message}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card title={isInbound ? 'Dobavljač' : 'Kupac'}>
          <Row label="Naziv" value={invoice.buyerName} />
          <Row label="PIB" value={invoice.buyerTaxId} />
          <Row label="Matični broj" value={invoice.buyerRegNumber} />
          {invoice.buyerJbkjs && <Row label="JBKJS" value={invoice.buyerJbkjs} />}
          <Row label="Adresa" value={invoice.buyerAddress} />
          <Row label="Grad" value={invoice.buyerCity} />
          {invoice.buyerEmail && <Row label="Email" value={invoice.buyerEmail} />}
          {invoice.isBudgetClient && <Row label="Budžetski korisnik" value="Da" />}
        </Card>

        <Card title="Dokument">
          <Row label="Broj" value={invoice.invoiceNumber} />
          <Row label="Tip" value={invoice.invoiceTypeCode} />
          <Row label="Valuta" value={invoice.currency} />
          <Row label="Datum izdavanja" value={fmtDate(invoice.issueDate)} />
          <Row label="Datum dospeća" value={fmtDate(invoice.dueDate)} />
          <Row label="Datum isporuke" value={fmtDate(invoice.deliveryDate)} />
          <Row label="Način plaćanja" value={invoice.paymentMeansCode} />
          {invoice.bankAccount && <Row label="Račun" value={invoice.bankAccount} />}
          {invoice.purchaseOrderRef && <Row label="Narudžbenica" value={invoice.purchaseOrderRef} />}
          {invoice.contractRef && <Row label="Ugovor" value={invoice.contractRef} />}
          {invoice.precedingInvoice && <Row label="Prethodna faktura" value={invoice.precedingInvoice} />}
        </Card>
      </div>

      <Card title="Stavke">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Naziv</th>
                <th style={th}>Šifra</th>
                <th style={{ ...th, textAlign: 'right' }}>Kol.</th>
                <th style={th}>JM</th>
                <th style={{ ...th, textAlign: 'right' }}>Cena</th>
                <th style={{ ...th, textAlign: 'right' }}>Popust %</th>
                <th style={th}>PDV</th>
                <th style={{ ...th, textAlign: 'right' }}>Osnovica</th>
                <th style={{ ...th, textAlign: 'right' }}>PDV iznos</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.id}>
                  <td style={td}>{l.lineNumber}</td>
                  <td style={{ ...td, color: 'var(--color-text-strong)' }}>{l.productName}</td>
                  <td style={{ ...td, color: 'var(--color-text-muted)' }}>{l.productCode || '-'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.quantity)}</td>
                  <td style={td}>{l.unitCode}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.unitPrice)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.discountPct || '0'}</td>
                  <td style={td}>{l.vatCategory} / {l.vatPercent}%</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.lineTotal)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(l.vatAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={8} style={{ ...td, textAlign: 'right', fontWeight: 600 }}>Ukupno bez PDV-a</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(invoice.totalWithoutVat)}</td>
                <td style={td} />
              </tr>
              <tr>
                <td colSpan={8} style={{ ...td, textAlign: 'right', fontWeight: 600 }}>PDV</td>
                <td style={td} />
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(invoice.totalVat)}</td>
              </tr>
              <tr>
                <td colSpan={8} style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--color-text-strong)' }}>Za plaćanje</td>
                <td colSpan={2} style={{ ...td, textAlign: 'right', fontWeight: 800, fontSize: 15, color: 'var(--color-text-strong)', fontVariantNumeric: 'tabular-nums' }}>{fmt(invoice.totalWithVat)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {invoice.cancelComment && (
        <Card title="Otkazivanje">
          <Row label="Komentar" value={invoice.cancelComment} />
        </Card>
      )}

      {invoice.stornoNumber && (
        <Card title="Storno">
          <Row label="Storno broj" value={invoice.stornoNumber} />
          <Row label="Komentar" value={invoice.stornoComment} />
        </Card>
      )}

      {showXml && invoice.ublXml && (
        <Card title="UBL XML">
          <pre style={{ margin: 0, padding: 14, background: 'var(--color-bg)', borderRadius: 6, fontSize: 11, overflow: 'auto', maxHeight: 500, fontFamily: 'Menlo, Consolas, monospace', color: 'var(--color-text)' }}>
            {invoice.ublXml}
          </pre>
        </Card>
      )}

      <Card title="Meta">
        <Row label="Request ID" value={invoice.requestId} />
        <Row label="Version" value={invoice.version != null ? String(invoice.version) : null} />
        <Row label="Poslednja SEF izmena" value={invoice.lastModifiedUtc ? new Date(invoice.lastModifiedUtc).toLocaleString('sr-Latn-RS') : null} />
        <Row label="Poslednja sinhronizacija" value={invoice.lastSyncAt ? new Date(invoice.lastSyncAt).toLocaleString('sr-Latn-RS') : null} />
        <Row label="Kreirano" value={new Date(invoice.createdAt).toLocaleString('sr-Latn-RS')} />
      </Card>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', fontSize: 13, borderBottom: '1px dashed var(--color-border)' }}>
      <div style={{ flex: '0 0 180px', color: 'var(--color-text-muted)' }}>{label}</div>
      <div style={{ flex: 1, color: 'var(--color-text)' }}>{value || '-'}</div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnDanger: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const noticeBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 16 };
